import {
  BlurGlobalState,
  ConnectionStates,
  DevicesRequestStates,
  PreferredDevicesType,
  avSelectors,
  useAvContext,
} from "@minervaproject/avsubsystem";
import {
  Alert,
  Button,
  CameraIcon,
  CameraOffIcon,
  Checkbox,
  Dropdown,
  LoadingCircleIcon,
  Modal,
  ProfilePhoto,
  Tooltip,
  palette,
} from "@minervaproject/fds";
import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { PreclassCheckAlert } from "./preclass_check/preclass_check_alert";
import { LINKS, PreclassCheckMessages } from "./preclass_check/preclass_check_messages";
import { PreclassTestRunner } from "./preclass_check/preclass_test_runner";

import { AudioLevelMeter, AudioLevelTestStatus } from "components/av/audio_meter";
import { CircleButton } from "components/header/circle_button";
import { HardwareMonitorContext } from "contexts/hardware_monitor_context";
import { useCapabilities } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { PROFILE_PROPERTIES, useUserProperties } from "hooks/use_user_properties";
import {
  setBlurBackground,
  setUserIsPerformingAvCheck,
  setUserPreferredDevices,
} from "shared/reducers/classroom";
import { DropdownOptionType, PreclassCheckResultType } from "shared/types";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import {
  PreclassTestNameType,
  PreclassTestOutcomeType,
  PreclassTestResultMessageType,
  PreclassTestResultSeverityType,
} from "shared/types/preclass_check";
import { BandwidthModeType, RoleType } from "shared/types/user";
import { classroomSelectors, localSelectors } from "store";
import { createMonitoringEvent } from "store/reducers/monitoring";
import { selectUserPreferredCamera, selectUserPreferredMic } from "store/selectors/classroom";
import { playTestSoundAudio } from "utils/audio";
import { computeInitials, profilePhotoUrl } from "utils/user";

import "./preclass_check.scss";

type PreclassCheckProps = {
  buttonText: string;
  hideModal: () => void;
};
const getBlurAlert = (state: BlurGlobalState | undefined) => {
  switch (state) {
    case BlurGlobalState.SLOW_LOADING:
      return (
        <Alert className="mb5" kind="info" showClose={false} data-testid="blur-alert">
          <span className="bold">Slow download for background blur.</span>
          <span>
            &nbsp;Background blur is taking a while to load and will be applied once downloaded.
          </span>
        </Alert>
      );
    case BlurGlobalState.ERROR:
      return (
        <Alert className="mb5" kind="warn" showClose={false} data-testid="blur-alert">
          <span className="bold">Unable to load background blur.</span>
          <span>
            &nbsp;There was an error when loading background blur. The error has been logged.
          </span>
        </Alert>
      );
    default:
      return null;
  }
};

const getExtensionAlert = (outcome: PreclassTestOutcomeType | undefined) => {
  if (outcome === PreclassTestOutcomeType.FAIL) {
    return (
      <Alert className="mb5" kind="warn" showClose={false} data-testid="extension-alert">
        <span className="bold">Finish setting up your account.</span>
        <span>
          &nbsp;Please download the Forum Chrome extension to finish the account setup process.
        </span>
        <a
          target="_blank"
          href={LINKS.minervaExtensionUrl}
          title="A Chrome extension is required for running the preclass check."
          rel="noreferrer"
        >
          {" Download"}
        </a>
      </Alert>
    );
  }
  return null;
};

function PreclassCheck({ buttonText, hideModal }: PreclassCheckProps) {
  const dispatch = useAppDispatch();

  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const user = useUserProperties(
    currentUserId,
    PROFILE_PROPERTIES.concat(["blur_background", "bandwidth_mode", "is_recording_agent"]),
  );
  const userExists = !!user;
  const isPresentationView = useAppSelector(classroomSelectors.selectIsPresentationView());
  const hasSkipUrlParam = new URLSearchParams(window.location.search).has("skip-av-check");
  const shouldSkipAvCheck = user?.is_recording_agent || isPresentationView || hasSkipUrlParam;
  const isCaptioner = user?.role === RoleType.Captioner;
  const isAvEnabled = useAppSelector(classroomSelectors.selectCurrentUserIsAvEnabled());

  const hardwareMonitor = useContext(HardwareMonitorContext);
  const { canControlClassroom, isVisibleInClass } = useCapabilities();
  const preclassTestRunner = useRef(
    new PreclassTestRunner(dispatch, hardwareMonitor, currentUserId),
  ).current;
  const preclassCheckMessages = useRef(new PreclassCheckMessages()).current;
  const avConnection = useAvContext();
  const devices = avSelectors.useDeviceList() || [];
  const selectedDevices = avSelectors.useSelectedDevices();
  const devicesStates = avSelectors.useDevicesStates();
  const preferredCameraLabel = useAppSelector(selectUserPreferredCamera(currentUserId));
  const preferredMicLabel = useAppSelector(selectUserPreferredMic(currentUserId));
  const [preferredDevices, setPreferredDevices] = useState<PreferredDevicesType>({
    preferredCameraLabel: null,
    preferredMicLabel: null,
  });
  const selectedMic = selectedDevices.selectedMic;
  const blurState = avSelectors.useBlurState();
  const [lastMonitoringEventType, setLastMonitoringEventType] =
    useState<MonitoringEventTypeType | null>(null);
  const classId = useAppSelector(classroomSelectors.selectClassId());
  const allowStudentNoVideo = useAppSelector(classroomSelectors.selectAllowStudentNoVideo());
  const hideVideo = !selectedDevices.selectedCam;
  const mutedVideo = hideVideo;
  const noAudio = devicesStates.micState.request_state !== DevicesRequestStates.granted;
  const isReady = !!selectedMic;
  const isAudioOnly = user?.bandwidth_mode === BandwidthModeType.AudioOnly;
  const canEnterClass = useRef<boolean>(false);
  const [checkResultInfo, setCheckResultInfo] = useState<PreclassTestResultMessageType | null>(
    null,
  );
  const extensionTestResult = useAppSelector(
    localSelectors.selectPreclassCheckResultByName(PreclassTestNameType.EXTENSION),
  );
  const browserTestResult = useAppSelector(
    localSelectors.selectPreclassCheckResultByName(PreclassTestNameType.BROWSER),
  );

  const forumRequestTestResult = useAppSelector(
    localSelectors.selectPreclassCheckResultByName(PreclassTestNameType.FORUM_REQUEST),
  );

  const audioTestResult = useAppSelector(
    localSelectors.selectPreclassCheckResultByName(PreclassTestNameType.AUDIO_LEVEL),
  );

  const allTestResults = useAppSelector(localSelectors.selectPreclassCheckResults());

  const audioLevelStatusCallback = useCallback(
    (alert: AudioLevelTestStatus) => {
      if (audioTestResult?.outcome.toString() !== alert.toString()) {
        preclassTestRunner.testAudioLevel(alert);
      }
    },
    [audioTestResult, preclassTestRunner],
  );

  const cameraOptions = devices
    .filter((device) => device.kind === "videoinput")
    .map((device) => ({
      label: device.label,
      value: device.id,
    }));
  const micOptions = devices
    .filter((device) => device.kind === "audioinput")
    .map((device) => ({
      label: device.label,
      value: device.id,
    }));

  const audioOutputOptions = devices
    .filter((device) => device.kind === "audiooutput")
    .map((device) => ({
      label: device.label,
      value: device.id,
    }));

  const videoElement = useRef<HTMLVideoElement>(null);
  const [audioLevelStream, setAudioLevelStream] = useState<MediaStream | null>(null);

  const haveAllTestsRun = useCallback(
    (preclassCheckResults: PreclassCheckResultType) => {
      let requiredTests: PreclassTestNameType[];
      if (!isAvEnabled) {
        requiredTests = PreclassTestRunner.getRequiredTestsNoAv();
      } else if (isVisibleInClass) {
        requiredTests = PreclassTestRunner.getRequiredTests();
      } else {
        requiredTests = PreclassTestRunner.getRequiredTestsObserver();
      }
      // Check if all required tests have the state success or fail
      const result = requiredTests.every((testName) => {
        const testResult = preclassCheckResults[testName];
        return testResult && testResult.outcome !== PreclassTestOutcomeType.IN_PROGRESS;
      });
      return result;
    },
    [isAvEnabled, isVisibleInClass],
  );

  const getSuccessResultMessage = useCallback((): PreclassTestResultMessageType => {
    let message = "You're ready for class.";
    let action = "Review your settings below.";
    if (isCaptioner) {
      message = `You're ready to caption.`;
      action = "Click below to get started.";
    } else if (!isVisibleInClass) {
      message = `You're ready to observe class.`;
      action = "Click below to join the class as an observer.";
    } else if (!isAvEnabled) {
      message = "You're ready for class.";
      action = "Click below to enter class.";
    }

    return {
      alertKind: "success",
      message,
      action,
    };
  }, [isCaptioner, isAvEnabled, isVisibleInClass]);

  const anyTestFailed = (preclassCheckResults: PreclassCheckResultType) => {
    const result = Object.values(preclassCheckResults).some((testResult) => {
      return testResult.outcome === PreclassTestOutcomeType.FAIL;
    });
    return result;
  };

  const selectedCameraOption = cameraOptions.find((cameraOpt: { value: string }) => {
    return cameraOpt.value === selectedDevices.selectedCam?.id;
  });

  const selectedMicOption = micOptions.find((micOpt: { value: string }) => {
    return micOpt.value === selectedDevices.selectedMic?.id;
  });

  const selectedAudioOutput = audioOutputOptions.find((audioOutOpt: { value: string }) => {
    return audioOutOpt.value === selectedDevices.selectedAudioOutput?.id;
  });

  const updateCamera = (option: DropdownOptionType) => {
    avConnection?.selectCamera(option.value as string);
  };

  const updateMicrophone = (option: DropdownOptionType) => {
    avConnection?.selectMic(option.value as string);
  };

  const toggleBackgroundBlur = async () => {
    if (!userExists) return;
    const blur = await avConnection?.setLocalBackgroundBlur(!user.blur_background);
    dispatch(
      setBlurBackground({
        userId: currentUserId,
        blurBackground: blur,
      }),
    );
    dispatch(
      createMonitoringEvent({
        type: MonitoringEventTypeType.TOGGLE_BACKGROUND_BLUR,
        userId: currentUserId,
        contextData: { enabled: blur || false },
      }),
    );
  };

  const onEnterClass = () => {
    // make sure the devices present in the options are the ones selected
    if (!isAvEnabled || !isVisibleInClass) {
      dispatch(setUserIsPerformingAvCheck({ userId: currentUserId, isPerformingAvCheck: false }));
      hideModal();
      return;
    }
    if (selectedDevices.selectedCam) {
      avConnection?.selectCamera(selectedDevices.selectedCam?.id);
    }
    if (selectedDevices.selectedMic) {
      avConnection?.selectMic(selectedDevices.selectedMic?.id);
    }
    dispatch(setUserIsPerformingAvCheck({ userId: currentUserId, isPerformingAvCheck: false }));
    if (videoElement.current) {
      videoElement.current.srcObject = null;
      setAudioLevelStream(null);
    }

    const shouldUpdatePreferredDevices =
      !preferredDevices ||
      preferredDevices.preferredCameraLabel !== selectedDevices.selectedCam?.label ||
      preferredDevices.preferredMicLabel !== selectedDevices.selectedMic?.label;

    // The user has selected devices - save them as preferred
    if (shouldUpdatePreferredDevices) {
      window.flightLogger.log(
        `[PreclassCheck] Saving preferred devices: ${selectedDevices.selectedCam?.label}, ${selectedDevices.selectedMic?.label}`,
      );
      dispatch(
        setUserPreferredDevices({
          userId: currentUserId,
          preferredCameraLabel: selectedDevices.selectedCam?.label,
          preferredMicLabel: selectedDevices.selectedMic?.label,
        }),
      );
    }
    hideModal();
  };

  const toggleVideo = () => {
    if (avConnection && hasDevices) {
      avConnection.setLocalVideo(mutedVideo);
      dispatch(
        createMonitoringEvent({
          type: MonitoringEventTypeType.TOGGLE_CAMERA,
          userId: currentUserId,
          contextData: { enabled: !mutedVideo },
        }),
      );
    }
  };

  useEffect(() => {
    setPreferredDevices({
      preferredCameraLabel: preferredCameraLabel || null,
      preferredMicLabel: preferredMicLabel || null,
    });
  }, [preferredCameraLabel, preferredMicLabel]);

  useEffect(() => {
    dispatch(
      createMonitoringEvent({
        type: MonitoringEventTypeType.PRECLASS_CHECK_STARTED,
        userId: currentUserId,
        contextData: {},
      }),
    );
  }, [currentUserId, dispatch]);

  // Update the audio level stream
  useEffect(() => {
    const localMediaStream = avConnection?.getLocalMediaStream();
    if (
      avConnection &&
      selectedMic &&
      localMediaStream &&
      localMediaStream.getAudioTracks().length > 0
    ) {
      // compare audio tracks
      const newAudioTrackId = localMediaStream.getAudioTracks()[0].id;
      const oldAudioTrackId = audioLevelStream?.getAudioTracks()[0]?.id;
      const shouldUpdateMediaStream =
        audioLevelStream === null || oldAudioTrackId !== newAudioTrackId;

      if (shouldUpdateMediaStream) {
        const newMediaStream = new MediaStream();
        newMediaStream.addTrack(localMediaStream.getAudioTracks()[0]);
        setAudioLevelStream(newMediaStream);
      }
    }
  }, [audioLevelStream, avConnection, selectedMic]);

  // Update displayed mediastream when selected devices change
  useEffect(() => {
    const video = videoElement.current;
    const mediaStream = avConnection?.getLocalMediaStream();
    const videoMediaStream = video?.srcObject as MediaStream;
    if (
      !video ||
      !selectedDevices ||
      !isVisibleInClass ||
      !avConnection ||
      !mediaStream ||
      (videoMediaStream && videoMediaStream.id === mediaStream.id)
    )
      return;
    video.srcObject = mediaStream;
  }, [isVisibleInClass, selectedDevices, avConnection]);

  // Connect to the A/V service once we have a class Id and the connection is ready
  const connectionState = avSelectors.useConnectionState();
  const shouldConnect = !connectionState || connectionState === ConnectionStates.initial;

  useEffect(() => {
    if (avConnection && shouldConnect) {
      window.flightLogger.log("[PreclassCheck] Connecting to the A/V service");
      avConnection.connect();
    }
  }, [shouldConnect, avConnection, classId]);

  // Start monitoring devices if the browser is supported
  const hasDevices = micOptions.length > 0;
  useEffect(() => {
    if (!userExists) return;
    if (
      avConnection &&
      isVisibleInClass &&
      browserTestResult?.outcome === PreclassTestOutcomeType.SUCCESS &&
      forumRequestTestResult?.outcome === PreclassTestOutcomeType.SUCCESS &&
      !hasDevices
    ) {
      window.flightLogger.log(
        "[PreclassCheck] Starting monitoring devices",
        JSON.stringify(preferredDevices),
      );
      avConnection?.startMonitoringDevices(preferredDevices);
      avConnection.setLocalBackgroundBlur(user.blur_background);
    }
  }, [
    avConnection,
    isVisibleInClass,
    browserTestResult,
    forumRequestTestResult,
    hasDevices,
    user?.blur_background,
    userExists,
    preferredDevices,
  ]);

  // Update connection state
  useEffect(() => {
    if (avConnection && connectionState) {
      preclassTestRunner.testConnection(connectionState);
    }
  }, [avConnection, connectionState, preclassTestRunner]);

  // Run browser test and extension test
  useEffect(() => {
    // The recording agent does not run the tests
    if (shouldSkipAvCheck) {
      if (isVisibleInClass) {
        avConnection?.startMonitoringDevices(preferredDevices);
      }
      dispatch(setUserIsPerformingAvCheck({ userId: currentUserId, isPerformingAvCheck: false }));
      hideModal();
    }
    if (!browserTestResult) {
      window.flightLogger.log("[PreclassCheck] Running browser test", isAvEnabled);
      preclassTestRunner.testBrowser();
    } else if (!forumRequestTestResult) {
      window.flightLogger.log("[PreclassCheck] Running forum request test");
      preclassTestRunner.testForumRequest();
    } else if (
      !extensionTestResult ||
      extensionTestResult.outcome === PreclassTestOutcomeType.FAIL
    ) {
      window.flightLogger.log("[PreClassCheck] Running extension test");
      preclassTestRunner.testExtension();
    }
  }, [
    browserTestResult,
    forumRequestTestResult,
    preclassTestRunner,
    extensionTestResult,
    isAvEnabled,
    shouldSkipAvCheck,
    hideModal,
    dispatch,
    currentUserId,
    isVisibleInClass,
    avConnection,
    preferredDevices,
  ]);

  // run computer tests
  useEffect(() => {
    preclassTestRunner.testComputer();
  }, [preclassTestRunner]);

  // Check the devices with every change to deviceStates
  useEffect(() => {
    if (isVisibleInClass && isAvEnabled && devicesStates) {
      window.flightLogger.log("[PreclassCheck]Checking devices");
      preclassTestRunner.testDevices(devicesStates);
    }
  }, [devicesStates, isAvEnabled, isVisibleInClass, preclassTestRunner]);

  // create monitoring event when all the tests are done
  useEffect(() => {
    if (Object.keys(allTestResults).length === 0) return;
    if (haveAllTestsRun(allTestResults)) {
      window.flightLogger.log("[PreclassCheck] All tests are done, creating monitoring event");
      const monitoringType = anyTestFailed(allTestResults)
        ? MonitoringEventTypeType.PRECLASS_CHECK_FAIL
        : MonitoringEventTypeType.PRECLASS_CHECK_SUCCESS;

      // We can have several failures for the same type because of retries
      // We only want to create one even unless the type changes
      if (monitoringType === lastMonitoringEventType) {
        window.flightLogger.log("[PreclassCheck] Monitoring event already created");
        return;
      }
      setLastMonitoringEventType(monitoringType);
      dispatch(
        createMonitoringEvent({
          type: monitoringType,
          userId: currentUserId,
          contextData: allTestResults,
        }),
      );
    }
  }, [
    allTestResults,
    dispatch,
    currentUserId,
    lastMonitoringEventType,
    setLastMonitoringEventType,
    haveAllTestsRun,
    hideModal,
  ]);

  // Update the alert messages based on the test results
  useEffect(() => {
    // Not all tests have run - we will get early errors or progress messages
    let result = preclassCheckMessages.getHighestPriorityMessageFromResults(allTestResults);

    if (!result) {
      if (haveAllTestsRun(allTestResults)) {
        // All tests are over and there are no errors
        result = getSuccessResultMessage();
        canEnterClass.current = true;
      } else {
        // We are still running tests
        result = {
          alertKind: "info",
          message: "Preparing to enter class.",
          action: "Please wait a few seconds.",
        };
      }
    }
    const currentCheckResult = checkResultInfo ? checkResultInfo.message : "";
    if (result.message !== currentCheckResult) {
      setCheckResultInfo({
        alertKind: result.alertKind as "info" | "error" | "success",
        message: result.message,
        action: result.action,
        severity: result.severity,
        link: result.link,
      });
      if (haveAllTestsRun(allTestResults) && result.severity) {
        canEnterClass.current = false;
      }
    }
  }, [
    allTestResults,
    preclassCheckMessages,
    checkResultInfo,
    canEnterClass,
    dispatch,
    currentUserId,
    haveAllTestsRun,
    isAvEnabled,
    isVisibleInClass,
    getSuccessResultMessage,
  ]);

  if (!user) {
    return null;
  }

  const labelClasses = "body-s bold text-black-tint-40 block mb1";
  return (
    <Modal size={isVisibleInClass ? "lg" : "sm"} hideCloseButton style={{ zIndex: 10000 }}>
      <div>
        <div>
          {checkResultInfo ? <PreclassCheckAlert preclassAlert={checkResultInfo} /> : null}
          {getBlurAlert(blurState)}
          {getExtensionAlert(extensionTestResult?.outcome)}
          <form>
            {isAvEnabled && isVisibleInClass ? (
              <div className="flex" data-testid="preclass-check-av-section">
                <div className="video-container is-self mr5 bg-black-tint-10">
                  {hideVideo ? (
                    <ProfilePhoto
                      className="absolute-center"
                      size="xl"
                      src={profilePhotoUrl(user)}
                      initials={computeInitials(user)}
                    />
                  ) : (
                    <video autoPlay muted playsInline ref={videoElement} />
                  )}
                  {!isReady ? (
                    <LoadingCircleIcon className="absolute-center" width={100} height={100} />
                  ) : null}
                  <div className="video-buttons">
                    {canControlClassroom || allowStudentNoVideo ? (
                      <CircleButton
                        // tooltip="Toggle camera"
                        className="bg-black"
                        ariaLabel="Toggle camera"
                        mode={mutedVideo ? "red" : "transparent"}
                        onClick={toggleVideo}
                        disabled={isAudioOnly}
                        IconComponent={mutedVideo ? CameraOffIcon : CameraIcon}
                        iconColor={palette.blackTint40}
                      />
                    ) : null}
                  </div>
                  <div className="audio-meter mr5">
                    {audioLevelStream ? (
                      <AudioLevelMeter
                        stream={audioLevelStream}
                        statusCallback={audioLevelStatusCallback}
                      />
                    ) : null}
                  </div>
                </div>
                <div className="flex-auto">
                  <div className="mb4">
                    <label className={labelClasses} htmlFor="cameraOptions">
                      Camera
                    </label>
                    <Dropdown
                      name="cameraOptions"
                      id="cameraSelect"
                      fullWidth
                      isSearchable={false}
                      isDisabled={isAudioOnly || mutedVideo}
                      showDropdown
                      onChange={updateCamera}
                      options={cameraOptions}
                      value={selectedCameraOption}
                    />
                    <Checkbox
                      mt={3}
                      onChange={toggleBackgroundBlur}
                      value={user.blur_background}
                      disabled={blurState === BlurGlobalState.ERROR || mutedVideo}
                    >
                      Blur Background
                    </Checkbox>
                  </div>
                  <div className="mb4">
                    <label className={labelClasses} htmlFor="micOptions">
                      Microphone
                    </label>
                    <Dropdown
                      name="micOptions"
                      id="micSelect"
                      fullWidth
                      isSearchable={false}
                      isDisabled={noAudio}
                      showDropdown
                      onChange={updateMicrophone}
                      options={micOptions}
                      value={selectedMicOption}
                    />
                  </div>

                  <div className="mb4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <label className={labelClasses} htmlFor="audioOutputOptions">
                          Audio Output
                        </label>
                        <Tooltip
                          className="mb1 ml1"
                          backgroundColor={palette.blackTint20}
                          textColor={palette.white}
                          text="Please change your audio output in your system settings."
                        />
                      </div>
                      <Button
                        kind="text"
                        className="mb1"
                        onClick={() => {
                          playTestSoundAudio();
                        }}
                      >
                        <span className="body-s">Play Test Sound</span>
                      </Button>
                    </div>
                    <Dropdown
                      name="audioOutputOptions"
                      id="audioOutputSelect"
                      fullWidth
                      showDropdown
                      isDisabled
                      isSearchable={false}
                      options={audioOutputOptions}
                      value={selectedAudioOutput}
                    />
                  </div>
                </div>
              </div>
            ) : null}
            <Button
              inline
              mr={4}
              mt={6}
              disabled={!canEnterClass.current}
              onClick={onEnterClass}
              data-testid="enter-class-button"
            >
              {buttonText}
            </Button>
            {haveAllTestsRun(allTestResults) &&
            checkResultInfo?.severity === PreclassTestResultSeverityType.SKIPPABLE ? (
              <Button
                kind="text"
                inline
                mr={4}
                mt={6}
                onClick={onEnterClass}
                data-testid="enter-anyway-button"
              >
                {buttonText} Anyway
              </Button>
            ) : null}
          </form>
        </div>
      </div>
    </Modal>
  );
}

export { PreclassCheck };
